import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
type CookieSettings = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};
const COOKIE_NAME = 'cookie_consent';
function getCookieConsent(): CookieSettings | null {
  const match = document.cookie.match(new RegExp('(^| )' + COOKIE_NAME + '=([^;]+)'));
  if (match) {
    try {
      return JSON.parse(decodeURIComponent(match[2]));
    } catch (e) {
      return null;
    }
  }
  return null;
}
function setCookieConsent(settings: CookieSettings) {
  const value = JSON.stringify({
    ...settings,
    timestamp: Date.now()
  });
  const maxAge = 60 * 60 * 24 * 365; // 1 year
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}
function ToggleSwitch({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return <button role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-hotpink focus:ring-offset-2 ${checked ? 'bg-hotpink' : 'bg-gray-300'}`} data-id="element-45">
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-46" />
    </button>;
}
export function CookieConsent() {
  const {
    t
  } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [settings, setSettings] = useState<CookieSettings>({
    essential: true,
    analytics: false,
    marketing: false
  });
  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      setSettings(consent);
    }
  }, []);
  const handleAcceptAll = () => {
    const newSettings = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setSettings(newSettings);
    setCookieConsent(newSettings);
    setIsVisible(false);
    setShowModal(false);
  };
  const handleEssentialOnly = () => {
    const newSettings = {
      essential: true,
      analytics: false,
      marketing: false
    };
    setSettings(newSettings);
    setCookieConsent(newSettings);
    setIsVisible(false);
    setShowModal(false);
  };
  const handleSaveSettings = () => {
    setCookieConsent(settings);
    setIsVisible(false);
    setShowModal(false);
  };
  const openModal = () => {
    setShowModal(true);
  };
  const closeModal = () => {
    // If banner was visible, keep it visible when closing modal without saving
    if (!getCookieConsent()) {
      setIsVisible(true);
    }
    setShowModal(false);
  };
  return <>
      {/* Banner */}
      <AnimatePresence data-id="element-47">
        {isVisible && !showModal && <motion.div initial={{
        y: 100,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 100,
        opacity: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6 flex justify-center pointer-events-none" data-id="element-48">
            <div className="bg-surface border border-gray-200 shadow-2xl rounded-2xl p-6 max-w-2xl w-full pointer-events-auto flex flex-col gap-6" data-id="element-49">
              <div data-id="element-50">
                <h3 className="font-heading font-bold text-lg mb-2 text-text-primary" data-id="element-51">
                  {t.cookie.banner.title}
                </h3>
                <p className="text-text-secondary text-sm leading-relaxed" data-id="element-52">
                  {t.cookie.banner.text}{' '}
                  <Link to="/datenschutz" className="text-hotpink hover:underline" data-id="element-53">
                    {t.cookie.banner.privacyLink}
                  </Link>
                  .
                </p>
              </div>

              <div className="flex flex-col md:flex-row gap-3 w-full" data-id="element-54">
                <button onClick={handleEssentialOnly} className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-text-secondary text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-55">
                  {t.cookie.banner.essentialOnly}
                </button>
                <button onClick={openModal} className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-text-secondary text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-56">
                  {t.cookie.banner.settings}
                </button>
                <button onClick={handleAcceptAll} className="flex-1 px-4 py-2.5 rounded-lg bg-hotpink text-white text-sm font-medium hover:bg-hotpink/90 transition-colors shadow-lg shadow-hotpink/20" data-id="element-57">
                  {t.cookie.banner.acceptAll}
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence data-id="element-58">
        {showModal && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-59">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={closeModal} className="absolute inset-0 bg-black/50 backdrop-blur-sm" data-id="element-60" />

            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="relative bg-surface rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col" data-id="element-61">
              {/* Header */}
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-white" data-id="element-62">
                <h2 className="font-heading font-bold text-xl text-text-primary" data-id="element-63">
                  {t.cookie.modal.title}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-text-secondary" data-id="element-64">
                  <X className="w-5 h-5" data-id="element-65" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto custom-scrollbar" data-id="element-66">
                <p className="text-text-secondary text-sm mb-6 leading-relaxed" data-id="element-67">
                  {t.cookie.modal.description}
                </p>

                <div className="space-y-6" data-id="element-68">
                  {/* Essential */}
                  <div className="flex items-start justify-between gap-4" data-id="element-69">
                    <div data-id="element-70">
                      <h4 className="font-semibold text-text-primary mb-1" data-id="element-71">
                        {t.cookie.modal.essential.title}
                      </h4>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-72">
                        {t.cookie.modal.essential.description}
                      </p>
                    </div>
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-hotpink bg-hotpink/10 px-2 py-1 rounded" data-id="element-73">
                      {t.cookie.modal.essential.always}
                    </span>
                  </div>

                  <div className="h-px bg-gray-100 w-full" data-id="element-74" />

                  {/* Analytics */}
                  <div className="flex items-start justify-between gap-4" data-id="element-75">
                    <div data-id="element-76">
                      <h4 className="font-semibold text-text-primary mb-1" data-id="element-77">
                        {t.cookie.modal.analytics.title}
                      </h4>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-78">
                        {t.cookie.modal.analytics.description}
                      </p>
                    </div>
                    <div className="shrink-0 pt-1" data-id="element-79">
                      <ToggleSwitch checked={settings.analytics} onChange={checked => setSettings({
                    ...settings,
                    analytics: checked
                  })} label={t.cookie.modal.analytics.title} data-id="element-80" />
                    </div>
                  </div>

                  <div className="h-px bg-gray-100 w-full" data-id="element-81" />

                  {/* Marketing */}
                  <div className="flex items-start justify-between gap-4" data-id="element-82">
                    <div data-id="element-83">
                      <h4 className="font-semibold text-text-primary mb-1" data-id="element-84">
                        {t.cookie.modal.marketing.title}
                      </h4>
                      <p className="text-xs text-text-secondary leading-relaxed" data-id="element-85">
                        {t.cookie.modal.marketing.description}
                      </p>
                    </div>
                    <div className="shrink-0 pt-1" data-id="element-86">
                      <ToggleSwitch checked={settings.marketing} onChange={checked => setSettings({
                    ...settings,
                    marketing: checked
                  })} label={t.cookie.modal.marketing.title} data-id="element-87" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-100 bg-white flex flex-col sm:flex-row gap-3" data-id="element-88">
                <button onClick={handleSaveSettings} className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-text-secondary text-sm font-medium hover:bg-gray-50 transition-colors" data-id="element-89">
                  {t.cookie.modal.save}
                </button>
                <button onClick={handleAcceptAll} className="flex-1 px-4 py-2.5 rounded-lg bg-hotpink text-white text-sm font-medium hover:bg-hotpink/90 transition-colors shadow-lg shadow-hotpink/20" data-id="element-90">
                  {t.cookie.modal.acceptAll}
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}